import React, { useEffect, useState } from 'react'
import { Alert, VerticalStack } from '@moonpig/launchpad-components'
import { useAuthFetch } from '@moonpig/web-core-auth'
import { useStore } from '@moonpig/web-core-stores'
import { logger } from '@moonpig/web-core-monitoring'
import {
  CookieCategory,
  destroyBrowserCookie,
  setBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import { useFlag } from '@moonpig/web-purchase-flags'
import { authenticationOidcService } from './AuthenticationOidcService'
import { SocialLoginButton } from './SocialLoginButton'
import { useAuthenticationLocaleText } from '../../text-localisation'
import { oidcErrorLogIdentifier, OidcProvider } from './types'
import { configuredProvidersList } from './authUtils'
import { SocialLoginButtonsLoadError } from './SocialLoginButtonsLoadError'
import { PrivateEmailModal } from './PrivateEmailModal'
import {
  trackPrivateEmailModalCTA,
  trackPrivateEmailModalImpression,
} from './tracking'
import { SignInDivider } from './SignInDivider'

export const SocialLoginButtonsWrapper: React.FC<{
  returnUrl?: string
  handleAccountRecovery?: () => void
  availableProviders?: string[]
  setLoading?: (flag: boolean) => void
}> = ({ returnUrl, handleAccountRecovery, availableProviders, setLoading }) => {
  const featurePrivateEmailModalEnabled = useFlag(
    'customer-private-email-modal',
  )

  const localiseText = useAuthenticationLocaleText()
  const [pageError, setPageError] = useState('')
  const [providers, setProviders] = useState<OidcProvider[]>()
  const [showPrivateEmailModal, setShowPrivateEmailModal] = useState(false)
  const [modalCallbackProvider, setModalCallbackProvider] =
    useState<OidcProvider | null>(null)
  const authFetch = useAuthFetch()

  const {
    store: { id: region },
  } = useStore()

  const { getOidcProviders, start } = authenticationOidcService(authFetch)

  useEffect(() => {
    async function getProviders() {
      const response = await getOidcProviders()
      let configuredProviders = response.providers.filter(p =>
        configuredProvidersList.includes(p.id),
      )
      if (availableProviders && availableProviders.length > 0) {
        configuredProviders = configuredProviders.filter(p =>
          availableProviders.includes(p.id),
        )
      }
      setProviders(configuredProviders)
    }

    getProviders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const genericError = localiseText('auth_wizard.generic_error')

  const startSocialFlow = async (provider: OidcProvider) => {
    if (setLoading) {
      setLoading(true)
    }

    setPageError('')
    const response = await start({
      provider: provider.id,
      redirectUrl: `${window.location.origin}/${region}/account/login/callback`,
    })
    /* istanbul ignore next */
    const selectedProviderName =
      providers?.find(p => p.id === provider.id)?.name || ''

    if (response) {
      try {
        /* istanbul ignore else */
        if (returnUrl) {
          setBrowserCookie(
            'mnpg_return_url',
            returnUrl,
            {
              maxAge: 30 * 60,
              path: '/',
            },
            CookieCategory.STRICTLY_NECESSARY,
          )
        }
        setBrowserCookie(
          'mnpg_provider',
          provider.id,
          {
            maxAge: 30 * 60,
            path: '/',
          },
          CookieCategory.STRICTLY_NECESSARY,
        )
        setBrowserCookie(
          'mnpg_provider_name',
          selectedProviderName,
          {
            maxAge: 30 * 60,
            path: '/',
          },
          CookieCategory.STRICTLY_NECESSARY,
        )
        window.location.href = response.redirectBrowserTo

        /* istanbul ignore next */
        if (setLoading) {
          setTimeout(() => {
            setLoading(false)
          }, 3000)
        }
      } catch (error) /* istanbul ignore next */ {
        logger.fixToday(
          `${oidcErrorLogIdentifier} Start Error`,
          undefined,
          error,
        )
        setPageError(genericError)
        if (setLoading) setLoading(false)
      } finally {
        destroyBrowserCookie('mnpg_return_url')
        destroyBrowserCookie('mnpg_provider')
        destroyBrowserCookie('mnpg_provider_name')
      }
    } else {
      logger.fixToday(`${oidcErrorLogIdentifier} Start no response error`)
      setPageError(genericError)
      if (setLoading) setLoading(false)
    }
  }

  return (
    <>
      {providers?.length ? <SignInDivider /> : null}

      {!!providers && !providers.length ? (
        <SocialLoginButtonsLoadError
          handleAccountRecovery={handleAccountRecovery}
        />
      ) : (
        <>
          <VerticalStack spacing={5}>
            {providers?.map(provider => (
              <SocialLoginButton
                key={provider.id}
                provider={provider}
                onClick={async () => {
                  if (
                    featurePrivateEmailModalEnabled &&
                    provider.hasPrivateEmails
                  ) {
                    setShowPrivateEmailModal(true)
                    trackPrivateEmailModalImpression()
                    setModalCallbackProvider(provider)
                    return
                  }

                  return startSocialFlow(provider)
                }}
              />
            ))}
          </VerticalStack>

          <PrivateEmailModal
            isOpen={showPrivateEmailModal}
            callback={() => {
              setShowPrivateEmailModal(false)
              trackPrivateEmailModalCTA()
              if (modalCallbackProvider) {
                startSocialFlow(modalCallbackProvider)
              }
            }}
          />
        </>
      )}

      {pageError && (
        <Alert variant="error" mt={6}>
          {pageError}
        </Alert>
      )}
    </>
  )
}
